import { useEffect, useState } from 'react';
import { ObjectValues } from '../types/utility-types';
import { deepObjectFreeze } from 'utils/deep-object-freeze/deep-object-freeze';
import { AppLogger } from '@workivate/tho-web-shared';

const LocalStorageKeys = deepObjectFreeze({
  persistent: 'lw-persistent',
  redirectTo: 'redirectTo',
  testable: 'testable',
  feMockServerEnabled: 'feMockServerEnabled',
  dataDogEnabled: 'dataDogEnabled',
  dynatraceEnabled: 'dynatraceEnabled',
  clientId: 'clientId',
  clientIdExpiryDate: 'clientIdExpiryDate',
  debugLogsOn: 'debugLogsOn', // from the AppLogger class
} as const);
type LocalStorageKeysValues = ObjectValues<typeof LocalStorageKeys>;

/* useLocalStorage allows a functional component to write:
  const [clientIDExpiryDate, setClientIDExpiryDate] = useLocalStorage<string>(
    LocalStorageKeys.clientIDExpiryDate
  );

    and then use clientIDExpiryDate and setClientIDExpiryDate as if they were useState variables
    NOTE THAT IF YOU UPDATE THE KEY DIRECTLY, WITH setLocalStorage, useLocalStorage WILL NOT UPDATE
*/

// the other helper functions can be used outside of functional components

// We use localStorage for values that should persist when the page is refreshed
// For values that should not persist, we can use react state, e.g. useContext, useReducer, useState

const setLocalStorage = <T>(
  key: LocalStorageKeysValues,
  value: T = '' as T
): void => window?.localStorage?.setItem(key, JSON.stringify(value));

const getLocalStorage = <T>(key: LocalStorageKeysValues): T | null => {
  if (typeof window !== 'undefined') {
    const value = window.localStorage.getItem(key);
    if (value) {
      try {
        return JSON.parse(value);
      } catch (error) {
        AppLogger.logWithOptions({
          error,
          message: `getLocalStorage() could not parse the value for key '${key}' as JSON. The value will be used as a raw string. Note: single-quoted values (e.g. 'development') will not work; use either no quotes or double quotes instead.`,
        });

        return value as unknown as T;
      }
    }
  }
  return null;
};

const deleteLocalStorage = (key: LocalStorageKeysValues): void =>
  window?.localStorage?.removeItem(key);

const localStorageKeyExists = (key: LocalStorageKeysValues): boolean => {
  return typeof window !== 'undefined' && key in (window.localStorage || {});
};

function useLocalStorage<T>(
  key: LocalStorageKeysValues,
  initialValue: T | null = null
): [T | null, (value: T) => void] {
  const readValue = () => {
    try {
      const item = getLocalStorage<T>(key);
      return item ?? initialValue ?? null;
    } catch (error) {
      AppLogger.logWithOptions({
        error,
        message: `Error reading localStorage key "${key}"`,
      });
      return initialValue;
    }
  };
  const [storedValue, setStoredValue] = useState<T | null>(readValue);
  const setValue = (value: T) => {
    try {
      setLocalStorage<T>(key, value);
      setStoredValue(value);
    } catch (error) {
      AppLogger.logWithOptions({
        error,
        message: `Error setting localStorage key "${key}"`,
      });
    }
  };
  useEffect(() => {
    const value = readValue();
    if (value) {
      setStoredValue(value);
    }
  }, []);

  return [storedValue, setValue];
}

// this should be called on any redirect
function clearLocalStorageForSession() {
  deleteLocalStorage(LocalStorageKeys.clientIdExpiryDate);
  deleteLocalStorage(LocalStorageKeys.clientId);
}

export {
  useLocalStorage,
  LocalStorageKeys,
  setLocalStorage,
  getLocalStorage,
  deleteLocalStorage,
  localStorageKeyExists,
  clearLocalStorageForSession,
};
